import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

function TermsOfService() {
  return (
    <div className="terms-page">
      <div className="terms-hero">
        <div className="container">
          <h1 className="terms-title">Terms of Service</h1>
          <p className="terms-date">Last Updated: December 7, 2025</p>
        </div>
      </div>

      <div className="terms-content">
        <div className="container">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using CreateMyApp's website and services, you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Services Description</h2>
            <p>
              CreateMyApp provides professional web and mobile application development services, including but not limited to:
            </p>
            <ul>
              <li>Custom web application development</li>
              <li>iOS mobile application development</li>
              <li>Android mobile application development</li>
              <li>Cross-platform application development</li>
              <li>UI/UX design services</li>
              <li>Application maintenance and support</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. Client Responsibilities</h2>
            <p>As a client, you agree to:</p>
            <ul>
              <li>Provide accurate and complete information for project requirements</li>
              <li>Respond to requests for information in a timely manner</li>
              <li>Review and approve deliverables within agreed timeframes</li>
              <li>Make payments according to the agreed schedule</li>
              <li>Provide necessary access to systems and resources as required</li>
              <li>Own or have rights to all content provided for the project</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>4. Project Timeline and Deliverables</h2>
            <p>
              Project timelines are estimates based on the information provided at the start of the project. 
              Timelines may be adjusted if:
            </p>
            <ul>
              <li>Project scope changes are requested</li>
              <li>Required information or approvals are delayed</li>
              <li>Technical challenges arise that require additional time</li>
              <li>Third-party services or integrations cause delays</li>
            </ul>
            <p>
              We will communicate any timeline changes promptly and work with you to minimize delays.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Payment Terms</h2>
            <p>
              Payment terms are specified in individual project agreements. Generally:
            </p>
            <ul>
              <li>A deposit is required before project commencement</li>
              <li>Milestone payments may be required for larger projects</li>
              <li>Final payment is due upon project completion</li>
              <li>Invoices are payable within 14 days unless otherwise agreed</li>
              <li>Late payments may incur additional fees</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>6. Intellectual Property Rights</h2>
            <p>
              <strong>Client Content:</strong> You retain all rights to content, data, and materials you provide.
            </p>
            <p>
              <strong>Developed Work:</strong> Upon full payment, you receive ownership of the final deliverables, 
              excluding any pre-existing tools, frameworks, or proprietary code libraries used in development.
            </p>
            <p>
              <strong>Portfolio Rights:</strong> We reserve the right to showcase completed projects in our 
              portfolio unless a non-disclosure agreement specifies otherwise.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Warranties and Disclaimers</h2>
            <p>
              We warrant that services will be performed in a professional and workmanlike manner. However:
            </p>
            <ul>
              <li>We do not guarantee specific business results or outcomes</li>
              <li>We are not responsible for third-party service failures</li>
              <li>We do not guarantee compatibility with future platform updates</li>
              <li>Applications are provided "as is" after the warranty period expires</li>
            </ul>
            <p>
              Standard warranty period is 30 days after project delivery for bug fixes and issues arising 
              from our work. Extended support and maintenance agreements are available separately.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Limitation of Liability</h2>
            <p>
              CreateMyApp's liability is limited to the total amount paid for the specific project. 
              We are not liable for:
            </p>
            <ul>
              <li>Loss of business, revenue, or profits</li>
              <li>Data loss or corruption not caused by our negligence</li>
              <li>Third-party claims arising from your use of the deliverables</li>
              <li>Indirect, consequential, or incidental damages</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>9. Confidentiality</h2>
            <p>
              We respect the confidentiality of your business information and project details. 
              We will not disclose confidential information to third parties without your consent, 
              except as required by law or to perform the contracted services.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Project Changes and Cancellation</h2>
            <p>
              <strong>Scope Changes:</strong> Changes to project scope may affect timeline and costs. 
              We will provide estimates for approval before implementing changes.
            </p>
            <p>
              <strong>Cancellation:</strong> Either party may terminate the agreement with written notice. 
              Upon cancellation:
            </p>
            <ul>
              <li>Client pays for all work completed to date</li>
              <li>Client receives deliverables for completed milestones</li>
              <li>Deposits are non-refundable</li>
              <li>Unused prepaid funds may be refunded at our discretion</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>11. Support and Maintenance</h2>
            <p>
              Post-launch support and maintenance services are available separately. These may include:
            </p>
            <ul>
              <li>Bug fixes and technical support</li>
              <li>Platform and framework updates</li>
              <li>Security patches and updates</li>
              <li>Feature enhancements</li>
              <li>Performance optimization</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>12. Third-Party Services</h2>
            <p>
              Projects may require third-party services (hosting, APIs, payment processors, etc.). 
              You are responsible for:
            </p>
            <ul>
              <li>Obtaining and maintaining necessary accounts</li>
              <li>Paying for third-party service fees</li>
              <li>Complying with third-party terms of service</li>
              <li>Managing third-party service relationships</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>13. Compliance and Legal</h2>
            <p>
              You are responsible for ensuring your application complies with applicable laws and regulations, 
              including but not limited to:
            </p>
            <ul>
              <li>Data protection and privacy laws (GDPR, PIPEDA, etc.)</li>
              <li>Accessibility standards</li>
              <li>Industry-specific regulations</li>
              <li>Terms of service for app stores and platforms</li>
            </ul>
            <p>
              We can provide guidance and implement technical requirements but cannot provide legal advice.
            </p>
          </section>

          <section className="terms-section">
            <h2>14. Dispute Resolution</h2>
            <p>
              In the event of a dispute, both parties agree to:
            </p>
            <ul>
              <li>Attempt to resolve the matter through good faith negotiation</li>
              <li>Consider mediation before pursuing legal action</li>
              <li>Submit to the jurisdiction of courts in British Columbia, Canada</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms of Service at any time. Changes will be posted 
              on this page with an updated revision date. Continued use of our services after changes 
              constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>Contact Information</h2>
            <p>
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <p className="contact-info">
              📧 <a href="mailto:info@createmyapp.ca">info@createmyapp.ca</a><br />
              📱 604-773-1594<br />
              📍 Surrey, BC, Canada
            </p>
          </section>

          <div className="back-button-container">
            <Link to="/" className="back-button">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
